import { RavenAvatar } from "@ravenappdev/raven-ui";
import type { AvatarProps as MuiAvatarProps } from "@mui/material/Avatar";

export type AvatarSize = "small" | "medium" | "large";

export interface AvatarProps extends Omit<MuiAvatarProps, "sx"> {
  size?: AvatarSize;
  gradient?: boolean;
  sx?: MuiAvatarProps["sx"];
}

// ESS's own scale. The package's `raven-avatar--*` classes carry 24/40/56
// and a purple fill, and land only when `raven-ui.css` is loaded — which
// ESS deliberately does not do (CLAUDE.md §6). These stay authoritative.
const SIZE_PX: Record<AvatarSize, number> = {
  small: 28,
  medium: 36,
  large: 48,
};

export default function Avatar({
  size = "medium",
  gradient,
  sx,
  ...rest
}: AvatarProps) {
  const px = SIZE_PX[size];
  return (
    <RavenAvatar
      size={size}
      gradient={gradient}
      sx={[
        {
          width: px,
          height: px,
          fontSize: size === "small" ? 12 : size === "large" ? 18 : 14,
          // Raven brand avatar gradient. The package theme has no
          // `gradients.orgAvatarBrand` token yet, so it stays literal here
          // and opts out of the `lint:tokens` gate.
          background: gradient
            ? "linear-gradient(311deg, #BF40AE 0%, #6B40BF 50%, #4E40BF 100%)" // allow-hex
            : undefined,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    />
  );
}
